import { updateState, withDevtools } from '@angular-architects/ngrx-toolkit';
import { signalStore, withMethods, withState } from '@ngrx/signals';

import { KeyValueDescriptionType } from '@models/common/keyValueModel';
import { SearchViewModel } from '@models/module/grid';
import { DocumentDetailsSessionActions, sessionInitialState, SIDEBAR_PAGE_SIZE } from '..';

/** Root-scoped counterpart of `DocumentDetailsStore`, for the state that must survive a closing dialog. */
export const DocumentDetailsSessionStore = signalStore(
  { providedIn: 'root' },
  withState(sessionInitialState),
  withDevtools('dmSessionStore'),

  withMethods(store => ({
    setStartScreens: (startScreens: KeyValueDescriptionType<number>[], moduleId: number) =>
      updateState(store, DocumentDetailsSessionActions.SetStartScreens, {
        startScreens,
        startScreensModuleId: moduleId,
      }),

    setSidebarSearchViewModel: (searchViewModel: SearchViewModel) =>
      updateState(store, DocumentDetailsSessionActions.SetSidebarSearchViewModel, { searchViewModel }),

    /**
     * Hands the grid's own query over to the sidebar a caller is about to open. The sidebar can only
     * scroll to a row it has already loaded, so the first page has to cover every row the grid had
     * loaded — `pagesize × page`, and never less than one full page.
     */
    seedSidebarSearch: (searchViewModel: SearchViewModel) =>
      updateState(store, DocumentDetailsSessionActions.SeedSidebarSearch, {
        searchViewModel: {
          ...searchViewModel,
          page: 1,
          pagesize: Math.max(searchViewModel.pagesize * searchViewModel.page, SIDEBAR_PAGE_SIZE),
        },
      }),

    setDocumentAsDirty: (entityInstanceId: number) => {
      if (store.dirtyDocuments().includes(entityInstanceId)) {
        return;
      }
      updateState(store, DocumentDetailsSessionActions.SetDocumentAsDirty, ({ dirtyDocuments }) => ({
        dirtyDocuments: [...dirtyDocuments, entityInstanceId],
      }));
    },

    setReloadTable: (reloadTable: boolean) =>
      updateState(store, DocumentDetailsSessionActions.SetReloadTable, { reloadTable }),

    /** Keeps `startScreens`, which describes the module rather than the dialog that just closed. */
    clearStore: () =>
      updateState(store, DocumentDetailsSessionActions.ClearStore, ({ startScreens, startScreensModuleId }) => ({
        ...sessionInitialState,
        startScreens,
        startScreensModuleId,
      })),

    /**
     * Drops a previous dialog's leftovers, keeping `searchViewModel` — which the caller seeds just
     * before the dialog is created, and which `clearStore` would reset.
     */
    clearDialogState: () =>
      updateState(store, DocumentDetailsSessionActions.ClearDialogState, {
        dirtyDocuments: [],
        reloadTable: false,
      }),
  })),
);
